import { useMemo } from 'react';

import { gql, useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import selectWorkspaceProducerName from 'selectors/workspaceProducerName';

import { ProductSelection } from '../model/types';

/** Same document shape as `useMiniGlp`, so both share the Apollo cache entry. */
export const PRODUCT_CATALOG_QUERY = gql`
  query MiniGlp {
    allMiniGlps {
      nodes {
        productId
        miniGlpJson
      }
    }
  }
`;

interface MiniGlpJson {
  product_id: number[] | number;
  product: string[] | string;
  producer: string[] | string;
  category_name?: string[] | string | null;
  reviews: number[] | number;
}

export interface ProductCatalogResponse {
  allMiniGlps: {
    nodes: Array<{ productId: number; miniGlpJson: MiniGlpJson }>;
  };
}

const first = <T,>(v: T[] | T | null | undefined): T | undefined =>
  Array.isArray(v) ? v[0] : (v ?? undefined);

export const toProductSelection = (row: MiniGlpJson): ProductSelection => {
  const category = first(row.category_name);
  return {
    id: Number(first(row.product_id)),
    name: String(first(row.product) ?? '').trim(),
    workspace: String(first(row.producer) ?? ''),
    category: category ? String(category) : null,
    reviews: Number(first(row.reviews)) || 0,
  };
};

/**
 * Products from the active workspace first, then newest first. The catalog has
 * no dates, but product ids are assigned sequentially, so a higher id is newer.
 */
export const sortCatalog = (
  catalog: ProductSelection[],
  /** Workspace name from the untyped Redux selector; only compared for equality. */
  activeWorkspace?: unknown,
): ProductSelection[] =>
  [...catalog].sort(
    (a, b) =>
      Number(b.workspace === activeWorkspace) - Number(a.workspace === activeWorkspace) || b.id - a.id,
  );

export interface ProductCatalog {
  catalog: ProductSelection[];
  /** Every workspace row for a product name. */
  byName: Map<string, ProductSelection[]>;
  loading: boolean;
  error?: Error;
}

/** The miniGLP product list reshaped for the competitive set picker, in `sortCatalog` order. */
const useProductCatalog = ({ skip = false } = {}): ProductCatalog => {
  const { data, loading, error } = useQuery<ProductCatalogResponse>(
    PRODUCT_CATALOG_QUERY,
    { skip },
  );
  const activeWorkspace = useSelector(selectWorkspaceProducerName);

  return useMemo(() => {
    const catalog = sortCatalog(
      (data?.allMiniGlps.nodes ?? [])
        .map((n) => toProductSelection(n.miniGlpJson))
        .filter((p) => p.name && Number.isFinite(p.id)),
      activeWorkspace,
    );
    const byName = new Map<string, ProductSelection[]>();
    catalog.forEach((p) => byName.set(p.name, [...(byName.get(p.name) ?? []), p]));
    return { catalog, byName, loading, error };
  }, [data, loading, error, activeWorkspace]);
};

export default useProductCatalog;
